import {
  type AutocompleteCategory,
  type AutocompleteSuggestionsQuery,
} from '@kijiji/generated/graphql-types'

import { type ApiLocale, API_LOCALE } from '@/domain/locale'
import { type SearchBarOption } from '@/ui/molecules/search-bar'
import { capitalizeFirstWord, capitalizeString } from '@/utils/string'

type AutocompleteSuggestionT = AutocompleteSuggestionsQuery['autocompleteSuggestions']

/**
 * Accepts the GraphQL suggestions array directly and returns an object
 * with generalResults set to the entire array and an empty autosResults array.
 */
export const separateInternalSuggestionsByType = (
  suggestions: AutocompleteSuggestionT
): {
  generalResults: AutocompleteSuggestionT
  autosResults: []
} => {
  // For GraphQL, all suggestions are treated as general results.
  return { generalResults: suggestions, autosResults: [] }
}

/**
 * Returns a category name for GraphQL suggestions.
 * This function replicates the localized name formatting logic from getSuggestedCategoryName.
 */
const getSuggestedCategoryName = (localizedName: string, apiLocale: ApiLocale): string => {
  if (apiLocale === API_LOCALE.fr_CA) {
    return capitalizeFirstWord(localizedName)
  }
  return capitalizeString(localizedName)
}

/**
 * Creates category options from the categories available in a GraphQL suggestion.
 * Note: GraphQL suggestions only provide id and localizedName, so we assume no parent info.
 */
export const getInternalSuggestedCategoryOptions = (
  apiLocale: ApiLocale,
  categories: AutocompleteCategory[],
  keyword: string
): SearchBarOption[] => {
  if (!categories.length) return []
  const localizedIn = apiLocale === API_LOCALE.en_CA ? 'in' : 'dans'

  return categories?.map((cat) => {
    const suggestedCategoryLabel = `${keyword} ${localizedIn} ${getSuggestedCategoryName(
      cat.localizedName,
      apiLocale
    )}`
    return {
      label: suggestedCategoryLabel,
      value: keyword,
      category: {
        id: parseInt(cat.id, 10),
        localizedName: cat.localizedName,
        parentId: null, // Since GraphQL doesn't provide parent info
      },
    }
  })
}

/**
 * Transforms GraphQL general suggestions into search bar options.
 * This function mimics the behavior of getCategorySuggestions from the Algolia version.
 */
export const getInternalCategorySuggestions = (
  generalResults: AutocompleteSuggestionT,
  apiLocale: ApiLocale = API_LOCALE.en_CA
): SearchBarOption[] => {
  if (!generalResults.length) return []

  // Get keyword and category of the first search result
  const { completion, categories } = generalResults[0]
  const firstCategoryArr = categories?.length > 0 ? [categories[0]] : [] // Only consider the first category
  const suggestedCategoryOptions = getInternalSuggestedCategoryOptions(
    apiLocale,
    firstCategoryArr,
    completion
  )

  // Map each suggestion to a plain search option.
  const suggestionsOptions: SearchBarOption[] = generalResults.map((item) => ({
    label: item.completion,
    value: item.completion,
  }))

  // Insert the single suggested category option (if any) into the second position of the results/options array.
  suggestionsOptions.splice(1, 0, ...suggestedCategoryOptions)
  return suggestionsOptions
}
